import { formatCurrencyWhole } from '../utils/api';
import Icon from './Icon';

// Progress feedback while you classify. That is the whole job.
//
// It grew into a small dashboard — percentage, tagged, total, remaining, a
// link, a bar with labelled ends, a footnote — and read as messy because it
// was answering a question nobody asks HERE. The case for classifying is made
// on ניתוח רכש, which already carries its own banner ("₪33,020 (47%) מההוצאה
// לא משויכת לאף סיווג") and a שייך פריטים button pointing at this screen. By
// the time anyone sees this component they have been convinced; restating the
// argument is noise on top of the work.
//
// So what is left is one number that moves as you work, and the bar that
// shows it moving. Everything else was either derivable or already on screen:
//
//   "₪37,774 מתוך ₪70,894" — three money figures to convey one ratio. "מהרכש"
//   is the denominator in words, and the exact totals belong to ניתוח רכש.
//
//   "נותרו ₪33,020 לסיווג ←" — the ללא סיווג chip sits directly below with
//   the same amount and the same filter behind it. One action, one place.
// Twenty blocks, one per 5%. Enough to read "about half" at a glance without
// counting, few enough that each block stays wide enough to see.
//
// This bar briefly lived in a shared CoverageSegments component, for a meter on
// the by-classification card that has since been dropped as redundant. With one
// consumer left, the indirection only made the reader open a second file to
// find out what a `<CoverageSegments>` looks like.
const SEGMENTS = 20;

// Whether the meter reads "done", and the whole percent it shows. Exported so
// the collapsed card header can state the same number the band does, from the
// same arithmetic — two roundings of one figure is how a header and its own
// body start disagreeing by a point.
export function coverageStatus(spend) {
  if (!spend) return null;
  const { untagged = 0, total = 0, pct_tagged: pct = 0 } = spend;
  const done = untagged <= 0 && total > 0;
  return { done, shownPct: done ? 100 : Math.min(99, Math.round(pct)) };
}

// `open` defaults to true so every existing caller renders exactly as before.
// Passing false is the collapsed state of the card on /item-classifications:
// the BAR survives — it is the one glanceable answer — while the headline
// sentence folds away, because the card header is already stating that number.
export default function CoverageBand({ spend, open = true }) {
  if (!spend) return null;

  const { untagged = 0, untaggable = 0, total = 0, pct_tagged: pct = 0 } = spend;

  // The job is done when nothing taggable is left — NOT at 100%. Unmatched
  // invoice lines sit in the denominator and cannot be classified from this
  // screen, so a restaurant that has tagged every product it has still reads
  // 97%. Holding that user at "3% remaining" forever, with no action that could
  // ever close it, is the meter lying about whether there is work to do.
  const done = untagged <= 0 && total > 0;

  // Whole percent. A tenth of a point on a progress meter is noise nobody acts
  // on, and it made the headline read as a measurement rather than a status.
  //
  // Held at 99 while anything is still taggable: 99.6 rounds to 100, and a
  // meter reading 100% above a ללא סיווג chip still carrying money is the
  // screen contradicting itself. 100 belongs to the done state alone.
  const shownPct = done ? 100 : Math.min(99, Math.round(pct));

  // Blocks are a rounding of the percentage, so the two ends need protecting:
  // real progress must never draw as an empty bar, and an unfinished job must
  // never draw as a full one — the same reason the label is held at 99.
  const filledSegments = Math.min(
    pct > 0 ? Math.max(1, Math.round((pct / 100) * SEGMENTS)) : 0,
    SEGMENTS - 1,
  );

  // Only in the done state. This is the answer to the one question a finished
  // user is left with — "why does it not say 100%?" — and before then it is a
  // concept nobody needs to meet.
  const showUntaggable = done && untaggable > 0;

  return (
    <div className="cov-band">
      {open && (
      <div className="cov-head">
        {done ? (
          <p className="cov-headline cov-headline-done">
            <Icon name="check" size={18} />
            כל הרכש שניתן לסיווג מסווג
          </p>
        ) : (
          <p className="cov-headline">{shownPct}% מהרכש מסווג</p>
        )}
      </div>
      )}

      {!done && (
        // aria-hidden: the headline right above states the same value in
        // words, and a progressbar role would make a screen reader read the
        // percentage twice.
        <div className="cov-bar" aria-hidden="true">
          {Array.from({ length: SEGMENTS }, (_, i) => (
            <span key={i} className={`cov-seg${i < filledSegments ? ' is-on' : ''}`} />
          ))}
        </div>
      )}

      {open && showUntaggable && (
        <p className="cov-note">
          {formatCurrencyWhole(untaggable)} לא ניתן לשיוך — שורות בחשבונית שלא נמצא
          להן מוצר תואם בקטלוג, ולכן הכיסוי לא מגיע ל-100%
        </p>
      )}
    </div>
  );
}
